import React from 'react';
import { Modal, Input, Form, Radio, Button, message, Spin, Tag } from 'antd';
import { FormInstance } from 'antd/es/form';
import Matter from '../../../common/model/matter/Matter';
import Lang from '../../../common/model/global/Lang';
import MessageBoxUtil from '../../../common/util/MessageBoxUtil';
import StringUtil from '../../../common/util/StringUtil';
import './ApprovalModal.less';

interface IProps {
  matter: Matter | null;
  visible: boolean;
  onClose: () => void;
  onApprovalSuccess: () => void;
}

interface IState {
  loading: boolean;
  submitting: boolean;
}

class ApprovalModal extends React.Component<IProps, IState> {
  formRef = React.createRef<FormInstance>();

  constructor(props: IProps) {
    super(props);
    this.state = {
      loading: false,
      submitting: false
    };
  }

  componentDidMount() {
    if (this.props.visible && this.props.matter) {
      this.loadMatterDetail();
    }
  }

  componentDidUpdate(prevProps: IProps) {
    if (this.props.visible !== prevProps.visible) {
      if (this.props.visible && this.props.matter) {
        this.loadMatterDetail();
      }
    }
  }

  // 加载文件详情
  loadMatterDetail = () => {
    if (!this.props.matter) return;
    
    this.setState({ loading: true });
    this.props.matter.httpDetail(
      () => {
        this.setState({ loading: false });
        this.formRef.current?.setFieldsValue({
          approval: this.props.matter!.approval,
          remark: this.props.matter!.approvalRemark,
          outsendStatus: this.props.matter!.outsendStatus,
          outsendRemark: this.props.matter!.outsendRemark || ''
        });
      },
      (error: string) => {
        this.setState({ loading: false });
        MessageBoxUtil.error(error);
      }
    );
  };

  // 获取审批状态对应的显示文本
  getApprovalText(approval: number): string {
    let statusText = '';
    switch (approval) {
      case 0:
        statusText = Lang.t('matter.noneedApproval');
        break;
      case 1:
        statusText = Lang.t('matter.waitApproval');
        break;
      case 2:
        statusText = Lang.t('matter.approvalPass');
        break;
      case 3:
        statusText = Lang.t('matter.approvalReject');
        break;
      default:
        statusText = Lang.t('matter.unknown');
    }
    return statusText ? `: ${statusText}` : '';
  }

  // 获取外发状态对应的显示文本
  getOutsendStatusText(outsendStatus: number): string {
    let statusText = '';
    switch(outsendStatus) {
      case 0:
        statusText = Lang.t('matter.outsendPending');
        break;
      case 1:
        statusText = Lang.t('matter.exportAudit');
        break;
      case 2:
        statusText = Lang.t('matter.approvalPass');
        break;
      case 3:
        statusText = Lang.t('matter.approvalReject');
        break;
      default:
        statusText = Lang.t('matter.unknown');
    }
    return statusText ? `: ${statusText}` : '';
  }

  // 提交审批
  handleSubmit = async (values: any) => {
    if (!this.props.matter) return;
    
    this.setState({ submitting: true });
    
    const { matter } = this.props;
    matter.approval = values.approval;
    matter.approvalRemark = values.remark;
    matter.outsendStatus = values.outsendStatus;
    matter.outsendRemark = values.outsendRemark;

    matter.httpApproval(
      () => {
        this.setState({ submitting: false });
        MessageBoxUtil.success(Lang.t('operationSuccess'));
        this.props.onApprovalSuccess();
        this.props.onClose();
      },
      (error: string) => {
        this.setState({ submitting: false });
        MessageBoxUtil.error(error);
      }
    );
  };

  // 取消审批
  handleCancel = () => {
    this.props.onClose();
  };

  render() {
    const { visible, matter } = this.props;
    const { loading, submitting } = this.state;

    if (!matter) {
      return null;
    }

    return (
      <Modal
        title={Lang.t('matter.approvalTitle')}
        visible={visible}
        onCancel={this.handleCancel}
        footer={null}
        width={600}
        destroyOnClose
        className="approval-modal"
        bodyStyle={{ zIndex: 10001 }}
      >
        <Spin spinning={loading}>
          <div className="approval-modal-content">
          <div className="file-info">
            <h4>{Lang.t('matter.fileInfo')}</h4>
            <p><strong>{Lang.t('matter.fileName')}:</strong> {matter.name}</p>
            <p><strong>{Lang.t('matter.size')}:</strong> {StringUtil.humanFileSize(matter.size)}</p>
            <p>
              <strong>{Lang.t('matter.type')}:</strong> 
              {matter.dir ? Lang.t('matter.directory') : Lang.t('matter.file')}
            </p>
            <p>
              <strong>{Lang.t('matter.operator')}:</strong> 
              {matter.user?.username || Lang.t('unknown')}
            </p>
          </div>
            <Form
              ref={this.formRef}
              layout="vertical"
              onFinish={this.handleSubmit}
              initialValues={{
                approval: matter.approval,
                remark: matter.approvalRemark || '',
                outsendStatus: matter.outsendStatus,
                outsendRemark: matter.outsendRemark || ''
              }}
            >
              <h4>{Lang.t('matter.uploadApprovalStatus')}</h4>
              <Form.Item
                name="approval"
                label={<span>{Lang.t('matter.approvalStatus')}<span className="approval-label">{this.getApprovalText(matter.approval)}</span></span>}
                rules={[{ required: true, message: Lang.t('matter.approvalStatusRequired') }]}
              >
                {matter.approval === 1 && (
                  <Radio.Group>
                    <Radio value={2}>{Lang.t('matter.approvalPass')}</Radio>
                    <Radio value={3}>{Lang.t('matter.approvalReject')}</Radio>
                  </Radio.Group>
                )}
              </Form.Item>

              <Form.Item
                name="remark"
                label={Lang.t('matter.approvalRemark')}
              >
                <Input.TextArea 
                  rows={2} 
                  placeholder={Lang.t('matter.approvalRemarkPlaceholder')}
                  maxLength={500}
                  showCount
                />
              </Form.Item>

              <h4>{Lang.t('matter.outsendApprovalStatus')}</h4>
              <Form.Item
                name="outsendStatus"
                label={<span>{Lang.t('matter.approvalStatus')}<span className="approval-label">{this.getOutsendStatusText(matter.outsendStatus)}</span></span>}
                rules={[{ required: true, message: Lang.t('matter.outsendStatusRequired') }]}
              >
                {matter.outsendStatus === 1 && (
                  <Radio.Group>
                    <Radio value={2}>{Lang.t('matter.approvalPass')}</Radio>
                    <Radio value={3}>{Lang.t('matter.approvalReject')}</Radio>
                  </Radio.Group>
                )}
              </Form.Item>

              <Form.Item
                name="outsendRemark"
                label={Lang.t('matter.approvalRemark')}
              >
                <Input.TextArea 
                  rows={2} 
                  placeholder={Lang.t('matter.approvalRemarkPlaceholder')}
                  maxLength={500}
                  showCount
                />
              </Form.Item>

              <Form.Item className="text-right">
                <Button onClick={this.handleCancel} className="mr10">
                  {Lang.t('cancel')}
                </Button>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={submitting}
                >
                  {Lang.t('confirm')}
                </Button>
              </Form.Item>
            </Form>
          </div>
        </Spin>
      </Modal>
    );
  }
}

export default ApprovalModal;